import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGlobalReducer, useGlobalState } from "../../GlobalContext";
import { getPageData, PageDataError } from "../../api/GetPageData";
import { Get } from "../api/request";
import { DefaultFormId } from "../config/resolveView";
import { ViewParam, withParam } from "./entityPageUrl";
import { withRelatedRecords } from "./relatedRecords";
import { RecordDefinition } from "./recordDefinition.types";
import { RelatedEntity } from "./relatedEntity.types";
import { EntityRecordRead, ResolvedView } from "./EntityPageProvider.types";

// Read off the error rather than by `instanceof`, which doesn't survive the api
// module being mocked.
function isNotFoundError(caught: unknown) {
  return (
    typeof caught === "object" &&
    caught !== null &&
    (caught as { notFound?: unknown }).notFound === true
  );
}

/**
 * An organisation's one record of an entity. A Ticketing read is flattened as
 * `GetPageData` flattens one; anything else is already the record.
 */
async function readFixedRecord(dispatch: unknown, method: string) {
  const response = await Get(dispatch, method);
  const read = response?.data;

  if (!read || typeof read !== "object") {
    throw new PageDataError(`Failed to read ${method}`, false);
  }

  return (
    read.Fields ? { ...read.Fields, id: read.Id } : { ...read }
  ) as Record<string, unknown>;
}

/** The record with the related tables its definition reads from a service added to it. */
async function addServiceRelated(
  record: Record<string, unknown> | null,
  recordId: string,
  definition: RecordDefinition | undefined,
  dispatch: unknown
) {
  const reads = Object.entries(definition?.relatedReads ?? {});

  if (!record || !recordId || reads.length === 0) {
    return record;
  }

  const read = await Promise.all(
    reads.map(async ([relatedEntityName, readRows]): Promise<RelatedEntity> => {
      const rows = await readRows(recordId, dispatch).catch(() => null);

      return rows
        ? { entityName: relatedEntityName, entities: rows }
        : { entityName: relatedEntityName, entities: [], failed: true };
    })
  );
  const existing = (record.relatedEntities ?? []) as RelatedEntity[];

  return { ...record, relatedEntities: [...existing, ...read] };
}

/**
 * The record on the page and the view it is read for: reading it, re-reading it when
 * either changes, and the edits made to it before a save.
 */
export function useEntityRecord({
  entityName,
  definition,
  isAllowed,
}: {
  entityName: string;
  definition: RecordDefinition | undefined;
  isAllowed: boolean;
}) {
  const globalDispatch = useGlobalReducer();
  const globalState = useGlobalState();
  // Read through a ref: the global state changes on every notification, which must not
  // re-read the record.
  const globalStateRef = useRef(globalState);
  globalStateRef.current = globalState;
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [record, setRecord] = useState<Record<string, unknown> | null>(null);
  const [originalRecord, setOriginalRecord] =
    useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Separated from `error` so the page can name a missing record as one.
  const [notFound, setNotFound] = useState(false);
  // Drives the layout and, server side, which extra attributes are projected -
  // so changing it refetches. Seeded from the URL, which is how a refresh comes back on
  // the view that was on screen.
  const [formId, setFormId] = useState(
    () => searchParams.get(ViewParam) ?? DefaultFormId
  );
  // Which view the service resolved for this record and this user, and the ones it
  // offered. Null until a record has been read, and for entities with no views.
  const [resolvedView, setResolvedView] = useState<ResolvedView | null>(null);
  // Set before the URL takes a newly-created record's id, so the fetch effect below
  // doesn't fire - creating already returned the current data.
  const skipNextFetchRef = useRef(false);
  // The record id a view has been settled for. Until it is, the read names no view,
  // which is how it asks the service to resolve one. A view named in the URL is already
  // settled - the point of it being there is that the read asks for it by name.
  const defaultFormAppliedForIdRef = useRef<string | null>(
    searchParams.get(ViewParam) ? id : null
  );
  // The same, for a record not saved yet, which has no id to key it on.
  const newRecordViewSettledRef = useRef(Boolean(searchParams.get(ViewParam)));

  // `askServiceToResolve` reads without naming a view even when one is settled: the
  // retry path below, for when the named view is what the read choked on.
  const readRecord = useCallback(
    async (
      recordId: string,
      askServiceToResolve = false
    ): Promise<EntityRecordRead> => {
      const recordAdapter = definition?.recordAdapter;
      const adapter =
        recordAdapter &&
        ((fetched: Record<string, unknown>) =>
          recordAdapter(fetched, {
            organisation: globalStateRef.current?.organisation,
            config: globalStateRef.current?.config,
          }));

      if (definition?.readMethod) {
        const fixed = await readFixedRecord(globalDispatch, definition.readMethod);
        return { record: adapter ? adapter(fixed) : fixed, view: null };
      }

      if (definition?.readRecord) {
        const own = await definition.readRecord(recordId);

        if (!own) {
          throw Object.assign(new Error(`No ${entityName} ${recordId}`), {
            notFound: true,
          });
        }

        return { record: adapter ? adapter(own) : own, view: null };
      }

      const { record: fetched, view } = await getPageData(
        globalDispatch,
        entityName,
        recordId,
        // Naming no view asks the service to resolve one, which is not the same as
        // naming the default. Once settled, every later read names it, so a refresh
        // comes back on the form that is on screen.
        !askServiceToResolve && defaultFormAppliedForIdRef.current === recordId
          ? formId
          : undefined
      );

      const withRelated = await addServiceRelated(
        fetched,
        recordId,
        definition,
        globalDispatch
      );

      return {
        // No record to adapt is a missing record, which the page reports as one -
        // not something for an adapter to trip over.
        record: adapter && withRelated ? adapter(withRelated) : withRelated,
        // Absent for a warehouse entity, which has no views at all.
        view: (view ?? null) as ResolvedView | null,
      };
    },
    [globalDispatch, entityName, formId, definition]
  );

  /** What a read returned becomes both the record and the unedited copy of it. */
  const applyRead = useCallback(
    ({ record: adapted, view }: EntityRecordRead) => {
      setRecord(adapted);
      setOriginalRecord(adapted);
      setResolvedView(view);
    },
    []
  );

  const fetchRecord = useCallback(
    async (isCancelled: () => boolean) => {
      if (!isAllowed) {
        setLoading(false);
        return;
      }

      // No `id` means a new, unsaved record - nothing to read. The service still
      // decides which view it opens on, unless the page has no views at all. A fixed
      // record has no id to begin with, and is read all the same.
      if (!id && !definition?.readMethod) {
        const newRecord = definition?.newRecord;
        const blank =
          typeof newRecord === "function"
            ? await newRecord({
                organisation: globalStateRef.current?.organisation,
                config: globalStateRef.current?.config,
                dispatch: globalDispatch,
              })
            : newRecord ?? {};

        if (definition?.authorable === false) {
          if (!isCancelled()) {
            setRecord(blank);
            setOriginalRecord(blank);
            setLoading(false);
          }
          return;
        }

        setLoading(true);
        setError(false);
        setNotFound(false);

        try {
          const named = newRecordViewSettledRef.current;
          const { view } = await getPageData(
            globalDispatch,
            entityName,
            undefined,
            named ? formId : undefined
          );

          if (isCancelled()) {
            return;
          }

          setRecord(blank);
          setOriginalRecord(blank);
          setResolvedView((view ?? null) as ResolvedView | null);

          if (!named) {
            newRecordViewSettledRef.current = true;

            if (view?.resolvedViewId && view.resolvedViewId !== formId) {
              skipNextFetchRef.current = true;
              setFormId(view.resolvedViewId);
            }
          }
        } catch {
          if (!isCancelled()) {
            setError(true);
          }
        } finally {
          if (!isCancelled()) {
            setLoading(false);
          }
        }
        return;
      }

      setLoading(true);
      setError(false);
      setNotFound(false);

      try {
        const namedAView = defaultFormAppliedForIdRef.current === id;
        let read;

        try {
          read = await readRecord(id ?? "");
        } catch (caught) {
          // The read named a view and failed. A view the caller may not see, or one
          // whose layout cannot be read, must not cost them the record - so ask the
          // service to resolve one and come back on the first form they can have.
          // A missing record is not the view's fault, and is reported as itself.
          if (!namedAView || isNotFoundError(caught)) {
            throw caught;
          }

          defaultFormAppliedForIdRef.current = null;
          read = await readRecord(id ?? "", true);
        }

        if (!isCancelled()) {
          applyRead(read);

          if (defaultFormAppliedForIdRef.current !== id) {
            defaultFormAppliedForIdRef.current = id;

            if (read.view?.resolvedViewId) {
              // The service already read the record on that view's projection, so this
              // catches up without asking again.
              if (read.view.resolvedViewId !== formId) {
                skipNextFetchRef.current = true;
                setFormId(read.view.resolvedViewId);
              }
            }
          }
        }
      } catch (caught) {
        if (!isCancelled()) {
          setError(true);
          setNotFound(isNotFoundError(caught));
        }
      } finally {
        if (!isCancelled()) {
          setLoading(false);
        }
      }
    },
    [
      id,
      formId,
      isAllowed,
      readRecord,
      applyRead,
      definition,
      globalDispatch,
      entityName,
    ]
  );

  const onRefresh = useCallback(
    () => fetchRecord(() => false),
    [fetchRecord]
  );

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    let cancelled = false;
    fetchRecord(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchRecord]);

  /** For a save that has already read the record it is about to put in the URL. */
  const skipNextFetch = useCallback(() => {
    skipNextFetchRef.current = true;
  }, []);

  /**
   * Marks a record's view as already chosen, so its next read names it. For a record
   * just created on a view, which would otherwise be resolved again on its first read.
   */
  const settleView = useCallback((recordId: string) => {
    defaultFormAppliedForIdRef.current = recordId;
  }, []);

  const onFieldChange = useCallback((patch: Record<string, unknown>) => {
    setRecord((previous) => ({ ...previous, ...patch }));
  }, []);

  // Applied to the original too: the change is already saved, so it is no edit of the
  // user's. relatedEntities is never part of the save payload either way.
  const onRelatedRecordsChange = useCallback(
    (relatedEntityName: string, records: RelatedEntity["entities"]) => {
      const replace = (previous: Record<string, unknown> | null) =>
        withRelatedRecords(previous, relatedEntityName, records);

      setRecord(replace);
      setOriginalRecord(replace);
    },
    []
  );

  // The URL takes the view only once the user has picked one. A link copied before that
  // still lets the recipient's own resolution run rather than pinning them to ours.
  // Replaced rather than pushed, so Back leaves the record instead of stepping back
  // through every view that was looked at.
  const onSelectForm = useCallback(
    (key: string) => {
      newRecordViewSettledRef.current = true;
      setFormId(key);
      setSearchParams((previous) => withParam(previous, ViewParam, key), {
        replace: true,
      });
    },
    [setSearchParams]
  );

  return {
    id,
    record,
    originalRecord,
    setOriginalRecord,
    loading,
    error,
    notFound,
    formId,
    resolvedView,
    readRecord,
    applyRead,
    skipNextFetch,
    settleView,
    onRefresh,
    onFieldChange,
    onRelatedRecordsChange,
    onSelectForm,
  };
}
